import { useEffect, useState } from "react";
import Input from "../../../form/Input";
import useForm from "../../../../hooks/useForm";
import Button from "../../../form/Button";
import FileUploadSingle from "../../../form/FileUploadSingle";
import Textarea from "../../../form/Textarea";
import { toast } from "react-toastify";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  organizationAddEdit,
  organizationDetails,
} from "../../../../services/organizationService";
import FlexibleSelect from "../../../form/select/FlexibleSelect";
import FlexibleLanguageSelect from "../../../form/select/FlexibleLanguageSelect";

const validation = {
  // Organization Section - Required
  name: { required: true, message: "Company Name is required" },

  contactEmail: [
    {
      required: true,
      message: "Contact Email is required",
    },
    {
      pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
      message: "Enter Valid Email",
    },
  ],

  // Business Information Section - Required
  registrationNumber: {
    required: true,
    message: "Registration Number is required",
  },
  taxId: { required: true, message: "Tax ID is required" },

  // Admin Information Section - Required
  firstName: { required: true, message: "First Name is required" },
  lastName: { required: true, message: "Last Name is required" },
  email: [
    {
      required: true,
      message: "Email is required",
    },
    {
      pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
      message: "Enter Valid Email",
    },
  ],
};

const ManageOrganization = ({
  getResponse = () => {},
  close = () => {},
  data,
}) => {
  const [availableLanguages, setAvailableLanguages] = useState(["en"]);
  const [defaultLanguage, setDefaultLanguage] = useState("en");
  const [addresses, setAddresses] = useState([]);
  const [customErrors, setCustomErrors] = useState({});

  const {
    values,
    handleChange,
    handleSubmit,
    errors,
    setFieldsValue,
    formValidate,
  } = useForm({
    validationRules: validation,
  });

  const { data: details } = useQuery({
    queryKey: ["organization-details", data?._id],
    queryFn: async () => {
      const res = await organizationDetails({ id: data?._id });
      return res?.data;
    },
    enabled: !!data?._id,
  });

  // Transform API response to form values
  useEffect(() => {
    if (details?._id) {
      const lang = details?.defaultLanguage || "en";

      // Extract multilingual content (if exists, otherwise use root level fields)
      const contentData = details?.content?.[lang] || {};

      // Set form fields
      setFieldsValue({
        // Basic fields
        _id: details?._id,
        type: details?.type || "",
        slug: details?.slug || "",
        status: details?.status || "inactive",
        verified: details?.verified ?? false,
        isSuspended: details?.isSuspended ?? false,
        createdAt: details?.createdAt || "",

        // Multilingual content fields
        // Try content structure first, then fall back to root level fields
        name: contentData?.title || details?.name || "",
        description: contentData?.content || details?.content || "",

        // Contact info
        contactEmail: details?.contactEmail || "",
        email: details?.email || "",

        // User info - these might not exist in organization response
        firstName: details?.firstName || "",
        lastName: details?.lastName || "",
        gender: details?.gender || "",
        password: "", // Never populate password from API for security

        // Business info (flatten for form)
        registrationNumber: details?.businessInfo?.registrationNumber || "",
        taxId: details?.businessInfo?.taxId || "",
        vatNumber: details?.businessInfo?.vatNumber || "",
        employeeCount: details?.businessInfo?.employeeCount || "",

        // Social links (flatten for form)
        // Priority: socialLinks.website > root website
        website: details?.socialLinks?.website || details?.website || "",
        linkedin: details?.socialLinks?.linkedin || "",
        twitter: details?.socialLinks?.twitter || "",
        facebook: details?.socialLinks?.facebook || "",

        // Images
        logo: details?.logo?.url || details?.logo || "",
        coverImage: details?.coverImage?.url || details?.coverImage || "",
      });

      // Set languages
      setAvailableLanguages(details?.availableLanguages || ["en"]);
      setDefaultLanguage(lang);

      // Handle addresses
      if (
        details?.address &&
        Array.isArray(details.address) &&
        details.address.length > 0
      ) {
        setAddresses(
          details.address.map((addr) => ({
            name: addr?.name || "",
            address1: addr?.address1 || "",
            address2: addr?.address2 || "",
            city: addr?.city || "",
            state: addr?.state || "",
            country: addr?.country || "",
            zipcode: addr?.zipcode || "",
          })),
        );
      } else {
        // Initialize with empty array if no addresses
        setAddresses([]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [details]);

  // Transform form values to API payload
  const transformToApiPayload = (formValues) => {
    const formData = new FormData();

    // Add basic fields
    if (details?._id) {
      formData.append("id", details._id);
      // Make id accessible for service method check
      formData.id = details._id;
    }

    formData.append("type", "standard");
    formData.append("slug", formValues.slug || "");
    formData.append("status", formValues.status || "inactive"); // ✅ STATUS: Included in API payload
    formData.append("verified", formValues.verified ?? false);
    formData.append("isSuspended", formValues.isSuspended ?? false);
    formData.append("contactEmail", formValues.contactEmail || "");

    // Add user info
    formData.append("firstName", formValues.firstName || "");
    formData.append("lastName", formValues.lastName || "");
    formData.append("gender", formValues.gender || "");
    formData.append("email", formValues.email || "");
    if (formValues.password) {
      formData.append("password", formValues.password);
    }

    // Add multilingual content - ensure defaultLanguage is "en" if not set
    const contentLanguage = defaultLanguage || "en";
    formData.append(
      `content[${contentLanguage}][title]`,
      formValues.name || "",
    );
    formData.append(
      `content[${contentLanguage}][content]`,
      formValues.description || "",
    );

    // Add languages - ensure defaultLanguage defaults to "en"
    formData.append("defaultLanguage", contentLanguage);
    const langs = availableLanguages.length > 0 ? availableLanguages : ["en"];
    langs.forEach((lang) => {
      formData.append("availableLanguages[]", lang);
    });

    // Add businessInfo
    formData.append(
      "businessInfo[registrationNumber]",
      formValues.registrationNumber || "",
    );
    formData.append("businessInfo[taxId]", formValues.taxId || "");
    formData.append("businessInfo[vatNumber]", formValues.vatNumber || "");
    formData.append(
      "businessInfo[employeeCount]",
      formValues.employeeCount || "",
    );

    // Add socialLinks
    formData.append("socialLinks[website]", formValues.website || "");
    formData.append("socialLinks[linkedin]", formValues.linkedin || "");
    formData.append("socialLinks[twitter]", formValues.twitter || "");
    formData.append("socialLinks[facebook]", formValues.facebook || "");

    // Add addresses
    addresses.forEach((addr, index) => {
      formData.append(`address[${index}][name]`, addr.name || "");
      formData.append(`address[${index}][address1]`, addr.address1 || "");
      formData.append(`address[${index}][address2]`, addr.address2 || "");
      formData.append(`address[${index}][city]`, addr.city || "");
      formData.append(`address[${index}][state]`, addr.state || "");
      formData.append(`address[${index}][country]`, addr.country || "");
      formData.append(`address[${index}][zipcode]`, addr.zipcode || "");
    });

    // Add files
    if (formValues.logo && typeof formValues.logo === "object") {
      formData.append("logo", formValues.logo);
    }
    if (formValues.coverImage && typeof formValues.coverImage === "object") {
      formData.append("coverImage", formValues.coverImage);
    }

    return formData;
  };

  const { mutate, isPending: isLoading } = useMutation({
    mutationFn: async (val) => {
      const payload = transformToApiPayload(val);
      return await organizationAddEdit(payload);
    },
    onSuccess: (res) => {
      if (res?.status === 200 || res?.status === 201) {
        toast.success(res?.message);
        getResponse(res);
        return res;
      } else {
        toast.warning(res?.message || res?.error);
      }
      return res;
    },
    onError: (error) => {
      console.log(error);
      toast.error("Failed to save company");
    },
  });

  const handleAddressChange = (e, index) => {
    const { name, value } = e.target;
    const field = name.split("-")[0];
    setAddresses((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });

    if (field === "name" && customErrors[`address_${index}_name`]) {
      const newErrors = { ...customErrors };
      delete newErrors[`address_${index}_name`];
      if (value.trim() !== "") {
        delete newErrors.addresses;
      }
      setCustomErrors(newErrors);
    }
  };

  const languages = [
    { name: "English", value: "en", code: "GB" },
    { name: "Spanish", value: "es", code: "ES" },
    { name: "French", value: "fr", code: "FR" },
    { name: "German", value: "de", code: "DE" },
  ];

  return (
    <form onSubmit={(event) => handleSubmit(event, mutate)}>
      <div className="h-full p-4">
        <div className="grid grid-cols-12 gap-x-4 gap-y-4">
          {/* Organization Information Section */}
          <div className="col-span-12">
            <h3 className="text-lg font-semibold mb-3">Company Information</h3>
          </div>

          <div className="relative col-span-6">
            <Input
              label="Comapny Name"
              inputPlaceholder={"Enter Company Name"}
              inputName="name"
              value={values?.name || ""}
              onChange={handleChange}
              required={true}
              {...formValidate(errors, "name")}
            />
          </div>
          {/* 
          <div className="relative col-span-6">
            <FlexibleSelect
              label={"Organization Type"}
              placeholder="Enter Organization Type"
              selectedValue={values?.type}
              dropdownData={[
                { name: "System", value: "system" },
                { name: "Standard", value: "standard" },
                { name: "Enterprise", value: "enterprise" },
                { name: "Partner", value: "partner" },
              ]}
              onChange={(val) => {
                handleChange({ target: { name: "type", value: val } });
              }}
              required={true}
              {...formValidate(errors, "type")}
            />
          </div> */}

          {/* <div className="relative col-span-6">
            <FlexibleLanguageSelect
              dropdownData={languages}
              isSearch={true}
              selectedValue={availableLanguages}
              defaultSelected={defaultLanguage}
              type="multiple"
              onChange={(val) => setAvailableLanguages(val.length > 0 ? val : ["en"])}
              onDefaultChange={(def) => setDefaultLanguage(def || "en")}
            />
          </div> */}

          <div className="relative col-span-6">
            <Input
              label="Contact Email"
              inputPlaceholder={"Enter Contact Email"}
              inputName="contactEmail"
              inputType="email"
              value={values?.contactEmail || ""}
              onChange={handleChange}
              required={true}
              {...formValidate(errors, "contactEmail")}
            />
          </div>

          <div className="relative col-span-12">
            <Textarea
              label="Description"
              inputPlaceholder={"Enter Description"}
              inputName="description"
              inputValue={values?.description || ""}
              onChange={handleChange}
              {...formValidate(errors, "description")}
            />
          </div>

          {/* Business Information Section */}
          <div className="col-span-12 mt-4">
            <h3 className="text-lg font-semibold mb-3">Business Information</h3>
          </div>

          <div className="relative col-span-6">
            <Input
              label="Registration Number"
              inputPlaceholder={"Enter Registration Number"}
              inputName="registrationNumber"
              value={values?.registrationNumber || ""}
              onChange={handleChange}
              required={true}
              {...formValidate(errors, "registrationNumber")}
            />
          </div>

          <div className="relative col-span-6">
            <Input
              label="Tax ID"
              inputPlaceholder={"Enter Tax ID"}
              inputName="taxId"
              value={values?.taxId || ""}
              onChange={handleChange}
              required={true}
              {...formValidate(errors, "taxId")}
            />
          </div>

          <div className="relative col-span-6">
            <Input
              label="VAT Number"
              inputPlaceholder={"Enter VAT Number"}
              inputName="vatNumber"
              value={values?.vatNumber || ""}
              onChange={handleChange}
              {...formValidate(errors, "vatNumber")}
            />
          </div>

          <div className="relative col-span-6">
            <FlexibleSelect
              label={"Employee Count"}
              placeholder="Enter Employee Count"
              selectedValue={values?.employeeCount}
              dropdownData={[
                { name: "1-10", value: "1-10" },
                { name: "11-50", value: "11-50" },
                { name: "51-200", value: "51-200" },
                { name: "201-500", value: "201-500" },
                { name: "501-1000", value: "501-1000" },
                { name: "1000+", value: "1000+" },
              ]}
              onChange={(val) => {
                handleChange({ target: { name: "employeeCount", value: val } });
              }}
              required={true}
              {...formValidate(errors, "employeeCount")}
            />
          </div>

          {/* Admin Information Section */}
          <div className="col-span-12 mt-4">
            <h3 className="text-lg font-semibold mb-3">Admin Information</h3>
          </div>

          <div className="relative col-span-6">
            <Input
              label="First Name"
              inputPlaceholder={"Enter First Name"}
              inputName="firstName"
              value={values?.firstName || ""}
              onChange={handleChange}
              required={true}
              {...formValidate(errors, "firstName")}
            />
          </div>

          <div className="relative col-span-6">
            <Input
              label="Last Name"
              inputPlaceholder={"Enter Last Name"}
              inputName="lastName"
              value={values?.lastName || ""}
              onChange={handleChange}
              required={true}
              {...formValidate(errors, "lastName")}
            />
          </div>

          <div className="relative col-span-6">
            <FlexibleSelect
              label={"Gender"}
              placeholder="Select Gender"
              selectedValue={values?.gender}
              dropdownData={[
                { name: "Male", value: "male" },
                { name: "Female", value: "female" },
                { name: "Other", value: "other" },
              ]}
              onChange={(val) => {
                handleChange({ target: { name: "gender", value: val } });
              }}
              {...formValidate(errors, "gender")}
            />
          </div>

          <div className="relative col-span-6">
            <Input
              label="Email"
              inputPlaceholder={"Enter Email"}
              inputName="email"
              inputType="email"
              value={values?.email || ""}
              onChange={handleChange}
              required={true}
              {...formValidate(errors, "email")}
            />
          </div>

          <div className="relative col-span-6">
            <Input
              label="Password"
              inputPlaceholder={"Enter Password"}
              inputName="password"
              inputType="password"
              value={values?.password || ""}
              onChange={handleChange}
              {...formValidate(errors, "password")}
            />
          </div>

          {/* Social Links Section */}
          <div className="col-span-12 mt-4">
            <h3 className="text-lg font-semibold mb-3">Social Links</h3>
          </div>

          <div className="relative col-span-6">
            <Input
              label="Website"
              inputPlaceholder={"Enter Website URL"}
              inputName="website"
              value={values?.website || ""}
              onChange={handleChange}
              {...formValidate(errors, "website")}
            />
          </div>

          <div className="relative col-span-6">
            <Input
              label="LinkedIn"
              inputPlaceholder={"Enter LinkedIn URL"}
              inputName="linkedin"
              value={values?.linkedin || ""}
              onChange={handleChange}
              {...formValidate(errors, "linkedin")}
            />
          </div>

          <div className="relative col-span-6">
            <Input
              label="Twitter"
              inputPlaceholder={"Enter Twitter URL"}
              inputName="twitter"
              value={values?.twitter || ""}
              onChange={handleChange}
              {...formValidate(errors, "twitter")}
            />
          </div>

          <div className="relative col-span-6">
            <Input
              label="Facebook"
              inputPlaceholder={"Enter Facebook URL"}
              inputName="facebook"
              value={values?.facebook || ""}
              onChange={handleChange}
              {...formValidate(errors, "facebook")}
            />
          </div>

          {/* Images Section */}
          <div className="col-span-12 mt-4">
            <h3 className="text-lg font-semibold mb-3">Images</h3>
          </div>

          <div className="relative col-span-12">
            <FileUploadSingle
              label={"Upload Logo Image"}
              labelClasses={"!mb-2"}
              value={values?.logo}
              onChange={(file) =>
                handleChange({ target: { name: "logo", value: file } })
              }
              {...formValidate(errors, "logo")}
            />
          </div>

          <div className="relative col-span-12">
            <FileUploadSingle
              label={"Upload Cover Image"}
              labelClasses={"!mb-2"}
              value={values?.coverImage}
              onChange={(file) =>
                handleChange({ target: { name: "coverImage", value: file } })
              }
              {...formValidate(errors, "coverImage")}
            />
          </div>

          {/* Status Section */}
          <div className="col-span-12 mt-4">
            <h3 className="text-lg font-semibold mb-3">Status & Settings</h3>
          </div>

          {/* ✅ STATUS FIELD: Properly connected to form state and API */}
          <div className="relative col-span-6">
            <FlexibleSelect
              label={"Status"}
              placeholder="Select Status"
              selectedValue={values?.status}
              dropdownData={[
                // { name: "Pending", value: "pending" },
                { name: "Active", value: "active" },
                { name: "Inactive", value: "inactive" },
                // { name: "Suspended", value: "suspended" },
                // { name: "Banned", value: "banned" },
              ]}
              onChange={(val) => {
                handleChange({ target: { name: "status", value: val } });
              }}
              {...formValidate(errors, "status")}
            />
          </div>

          <div className="relative col-span-6">
            <FlexibleSelect
              label={"Verified"}
              placeholder="Select Verified Status"
              selectedValue={values?.verified}
              dropdownData={[
                { name: "Yes", value: true },
                { name: "No", value: false },
              ]}
              onChange={(val) => {
                handleChange({ target: { name: "verified", value: val } });
              }}
              {...formValidate(errors, "verified")}
            />
          </div>

          <div className="relative col-span-6">
            <FlexibleSelect
              label={"Suspended"}
              placeholder="Select Suspended Status"
              selectedValue={values?.isSuspended}
              dropdownData={[
                { name: "Yes", value: true },
                { name: "No", value: false },
              ]}
              onChange={(val) => {
                handleChange({ target: { name: "isSuspended", value: val } });
              }}
              {...formValidate(errors, "isSuspended")}
            />
          </div>
        </div>

        {/* Addresses Section */}
        <div className="col-span-12 mt-6">
          <h3 className="text-lg font-semibold mb-3">Addresses</h3>
        </div>

        <div className="space-y-4 mt-4">
          {addresses.map((addr, index) => (
            <div
              key={index}
              className="p-4 border border-gray-300 rounded-md space-y-2 bg-gray-50"
            >
              <div className="flex justify-between items-center mb-2">
                <h4 className="text-sm font-semibold">Address {index + 1}</h4>
                <button
                  type="button"
                  onClick={() => {
                    setAddresses((prev) => prev.filter((_, i) => i !== index));
                    if (addresses.length > 1 && customErrors.addresses) {
                      setCustomErrors({ ...customErrors, addresses: "" });
                    }
                  }}
                  className="text-red-500 text-xs hover:text-red-700"
                >
                  <i className="fas fa-trash"></i> Remove
                </button>
              </div>

              <div>
                <Input
                  label="Address Name"
                  inputName="name"
                  inputPlaceholder="Enter address name"
                  value={addr.name || ""}
                  onChange={(e) => handleAddressChange(e, index)}
                />
                {customErrors[`address_${index}_name`] && (
                  <p className="text-red-500 text-sm mt-1">
                    {customErrors[`address_${index}_name`]}
                  </p>
                )}
              </div>

              <Input
                label="Address Line 1"
                inputName="address1"
                inputPlaceholder="Enter address line 1"
                value={addr.address1 || ""}
                onChange={(e) => handleAddressChange(e, index)}
              />

              <Input
                label="Address Line 2"
                inputName="address2"
                inputPlaceholder="Enter address line 2"
                value={addr.address2 || ""}
                onChange={(e) => handleAddressChange(e, index)}
              />

              <div className="grid grid-cols-2 gap-4">
                <Input
                  label="City"
                  inputName="city"
                  inputPlaceholder="Enter city"
                  value={addr.city || ""}
                  onChange={(e) => handleAddressChange(e, index)}
                />
                <Input
                  label="State"
                  inputName="state"
                  inputPlaceholder="Enter state"
                  value={addr.state || ""}
                  onChange={(e) => handleAddressChange(e, index)}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <Input
                  label="Country"
                  inputName="country"
                  inputPlaceholder="Enter country"
                  value={addr.country || ""}
                  onChange={(e) => handleAddressChange(e, index)}
                />
                <Input
                  label="Zipcode"
                  inputName="zipcode"
                  inputPlaceholder="Enter zipcode"
                  value={addr.zipcode || ""}
                  onChange={(e) => handleAddressChange(e, index)}
                />
              </div>
            </div>
          ))}

          <Button
            buttonLabel="+ Add Address"
            buttonFunction={() => {
              setAddresses([
                ...addresses,
                {
                  name: "",
                  address1: "",
                  address2: "",
                  city: "",
                  state: "",
                  country: "",
                  zipcode: "",
                },
              ]);
              if (customErrors.addresses) {
                setCustomErrors({ ...customErrors, addresses: "" });
              }
            }}
            buttonClasses="!bg-secondary !text-white"
          />
          {customErrors.addresses && (
            <p className="text-red-500 text-sm mt-1">
              {customErrors.addresses}
            </p>
          )}
        </div>

        <div className="pt-3 pb-6 flex justify-end mt-5">
          <Button
            buttonType="submit"
            buttonClasses={"min-w-36"}
            buttonLabel={data?._id ? "Update Company" : "Save Company"}
            {...(isLoading && {
              loading: true,
              buttonIconPosition: "left",
              isDisable: true,
            })}
          />
        </div>
      </div>
    </form>
  );
};

export default ManageOrganization;
